// Mixer fader math — shared by the extension (master mix, jam volume/pan
// sync) and viz (per-lane volume display / playback gain).
//
// Live's mixer volume DeviceParameter is a fader POSITION in 0..1, not a
// linear gain: default ≈0.85 = 0 dB (unity), top = +6 dB, and the taper is
// roughly constant-dB-per-fader-distance. The panning DeviceParameter is
// -1..1 (hard left..hard right), default 0 = center. A send DeviceParameter
// is 0..1 as well but tops out at 0 dB (`sendToGain`). Pure math, no runtime
// deps.

/** Live's mixer fader at unity (0 dB) — the parameter's default value. */
export const UNITY_VOLUME = 0.85;

/** Live's panning knob at center — the parameter's default value. */
export const CENTER_PAN = 0;

const TOP_DB = 6; // fader at max (1.0)
const FLOOR_DB = -42; // fader near the bottom — approximates Live's taper in the usable range

/** Clamp a proposed fader position into the parameter's real range. A
 * non-finite value falls back to unity, never to silence. */
export function clampVolume(v: number): number {
  if (!Number.isFinite(v)) return UNITY_VOLUME;
  return Math.min(1, Math.max(0, v));
}

/** Clamp a proposed pan position into the knob's real -1..1 range. A
 * non-finite value falls back to center, never to a hard side. */
export function clampPan(v: number): number {
  if (!Number.isFinite(v)) return CENTER_PAN;
  return Math.min(1, Math.max(-1, v));
}

/** "C" / "L30" / "R7" — the display form of a pan position, Live-style
 * (percent of full deflection, no sign chatter at center). */
export function panLabel(value: number): string {
  const v = clampPan(value);
  const pct = Math.round(Math.abs(v) * 50);
  if (pct === 0) return "C";
  return `${v < 0 ? "L" : "R"}${pct}`;
}

/**
 * Map a fader position (0..1) to dB. Anchored at the known points — unity → 0 dB
 * and max → +6 dB — and dB-linear in fader distance on either side, so equal
 * fader moves give equal loudness changes. An approximation of Live's curve, not
 * its exact taper; good enough for a preview master and for display.
 */
export function faderToDb(value: number, unity: number = UNITY_VOLUME): number {
  const u = Number.isFinite(unity) && unity > 0 && unity < 1 ? unity : UNITY_VOLUME;
  if (value >= u) {
    // unity..max → 0..+6 dB
    return ((value - u) / (1 - u)) * TOP_DB;
  }
  // 0..unity → FLOOR_DB..0 dB
  return FLOOR_DB * (1 - value / u);
}

/**
 * The inverse of `faderToDb`: the fader position that reads as `db`. Above
 * +6 dB pins to the top; -inf, NaN and anything below the modelled floor pin
 * to the bottom (silence). Used to pull an Audiotool track's gain into a Live
 * fader.
 */
export function dbToFader(db: number, unity: number = UNITY_VOLUME): number {
  const u = Number.isFinite(unity) && unity > 0 && unity < 1 ? unity : UNITY_VOLUME;
  if (!Number.isFinite(db)) return db > 0 ? 1 : 0;
  if (db >= 0) return Math.min(1, u + (db / TOP_DB) * (1 - u));
  return Math.max(0, u * (1 - db / FLOOR_DB));
}

/**
 * Convert a fader position into a linear amplitude multiplier. A naive
 * `value / unity` barely moves the gain for normal fader cuts (a fader pulled
 * well down still reads ~0.7×) — map position → dB → gain instead.
 */
export function faderToGain(value: number, unity: number = UNITY_VOLUME): number {
  if (!Number.isFinite(value) || value <= 0) return 0;
  return Math.pow(10, faderToDb(value, unity) / 20);
}

/** "0.0 dB" / "-6.3 dB" / "-inf dB" — the display form of a fader position. */
export function faderDbLabel(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return "-inf dB";
  const db = faderToDb(value);
  return `${db > 0 ? "+" : ""}${db.toFixed(1)} dB`;
}

/** The display form of a plain linear gain (0..1, 1 = unity) — the browser's
 * master, which is a volume, not a Live fader. Read through faderDbLabel its
 * default of 1 showed "+6.0 dB" while the output was untouched. */
export function gainDbLabel(gain: number): string {
  if (!Number.isFinite(gain) || gain <= 0) return "-inf dB";
  const db = 20 * Math.log10(Math.min(1, gain));
  return `${Math.abs(db) < 0.05 ? "0.0" : db.toFixed(1)} dB`;
}

/**
 * Whether a track is heard under the mixer's gates, Live's way: a track
 * whose Track Activator is off has no output even when soloed, and while
 * anything is soloed only the soloed tracks pass. Solo never overrides mute.
 */
export function isAudible(muted: boolean, soloed: boolean, soloing: boolean): boolean {
  return !muted && (!soloing || soloed);
}

/** dB per unit of knob travel — the fader's slope below unity, reused. */
const SEND_DB_PER_UNIT = -FLOOR_DB / UNITY_VOLUME;

/**
 * Map a send knob position (0..1) to dB. A send is Live's mixer send
 * DeviceParameter, stored raw by the sync (clamped to 0..1, never
 * converted): its range is -inf..0 dB — the knob tops out at unity, unlike
 * the fader's +6 dB. Modelled with the fader's dB-linear slope below unity,
 * anchored at 1.0 = 0 dB (so 0.5 ≈ -25 dB, and a knob at the bottom is
 * silence). An approximation of Live's taper, like `faderToDb`.
 */
export function sendToDb(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return -Infinity;
  return SEND_DB_PER_UNIT * (Math.min(1, value) - 1);
}

/** A send knob position as a linear amplitude multiplier: 1.0 → unity, 0 → silence. */
export function sendToGain(value: number): number {
  const db = sendToDb(value);
  return Number.isFinite(db) ? Math.pow(10, db / 20) : 0;
}

/**
 * Per-side gains for Live's mixer pan: "constant power panning with
 * sinusoidal gain curves. Output is 0 dB at the center position and signals
 * panned fully left or right will be increased by +3 dB." θ runs 0..π/2
 * across the knob; √2 lifts the centre (cos π/4 = 1/√2) back to unity, so a
 * hard pan is √2 (+3 dB) on one side and 0 on the other. Applied per
 * channel — on a stereo signal it is a balance control, nothing folds.
 */
export function panGains(pan: number): { left: number; right: number } {
  const theta = ((clampPan(pan) + 1) * Math.PI) / 4;
  // Snap the float dust at the ends so a hard pan is exactly silent on the far side.
  const snap = (g: number): number => (Math.abs(g) < 1e-12 ? 0 : g);
  return { left: snap(Math.SQRT2 * Math.cos(theta)), right: snap(Math.SQRT2 * Math.sin(theta)) };
}
